import * as React from "react";
import { Route, IndexRoute, Redirect } from "react-router";

import { FeatureFlaggedComponent } from "components/shared";

// Middleware
import chainMiddleware, { chainOnChangeMiddeware } from "./middleware/chain-middleware";
import requireAuth from "./middleware/require-auth";
import sanitizeUrl from "./middleware/sanitize-url";
import requireFullSignIn from "./middleware/require-full-signin";
import setActiveBrandFromUrl from "./middleware/set-active-brand-from-url";
import setActiveBrandFallback from "./middleware/set-active-brand-fallback";
import setActiveBrandFromCache from "./middleware/set-active-brand-from-cache";
import requireBrand from "./middleware/require-brand";
import session from "./middleware/session";
import redirectToLastBrand from "./middleware/redirect-to-last-brand";
import redirectIfTempPassword from "./middleware/redirect-if-temp-password";
import redirectToRetail from "./middleware/redirect-to-retail";
import canAccessModule from "./middleware/can-access-module";
import redirectIfLoggedIn from "./middleware/redirect-if-logged-in";
import checkUserType from "./middleware/user-type";
import { activeCompany } from "./middleware/active-company";
import requestedConnections from "./middleware/requested-connections";
import requireNonBlockedUser from "./middleware/require-non-blocked-user";
import redirectToLegacy from "./middleware/redirect-to-legacy";
import removeActiveBrand from "./middleware/remove-active-brand";
import adminRoute from "./middleware/admin-visibility";
import hasPermission from "./middleware/has-permission";
import hasPOSIntegration from "./middleware/has-pos-integration";
import checkPreference from "./middleware/check-preference";
import onRouteChanged from "./middleware/route-changed";
import preferredRetailers from "./middleware/preferred-retailers";
import checkRetailerProfile from "./middleware/check-retailer-profile";
import redirectBrandUser from "./middleware/redirect-brand-user-on-assortment-routes";
import { hasLsProcessor, hasActiveProcessor } from "./middleware/has-nuorder-processor";
import { invoicesMenuItemAccess, trackInvoicePageAccess } from "./middleware/invoice-page-access";
import notImpersonating from "./middleware/not-impersonating";
import redirectToPermissions from "middleware/redirect-to-permissions";
import Loading from "components/loading";
import { ShimmerLoader } from "components/loading/shimmer-loader";
import Loadable from "react-loadable";
import { wrapOnChange } from "./middleware/utils";
import merchantEnablements from "./middleware/merchant-enablement";
import merchantManagement from "middleware/merchant-management";
import removeActiveCompany from "middleware/remove-active-company";

const DynamicLoadContainer = (loader) => {
    return Loadable({
        loader,
        loading: Loading,
    });
};

const DynamicShimmerLoader = (loader) => {
    return Loadable({
        loader,
        loading: ShimmerLoader,
    });
};

// Views
import App from "components/app";
const LegacyWholesale = DynamicLoadContainer(() => import("containers/legacy-wholesale"));
import LegacyContainer from "./containers/legacy";
import PageNotFound from "./containers/page-not-found";
import * as warning from "./middleware/warning-redirect";
import MerchantIsDeactive from "./containers/merchant-is-deactive";

const InventoryDownloadContainer = DynamicLoadContainer(() => import("./containers/inventory-download"));
const ItemsDownloadContainer = DynamicLoadContainer(() => import("./containers/items-download"));
const OrderDownloadContainer = DynamicLoadContainer(() => import("./containers/order-download"));
const AtsDownloadContainer = DynamicLoadContainer(() => import("./containers/ats-download"));
const BlankContainer = DynamicLoadContainer(() => import("./containers/blank"));
const ReportingPage = DynamicLoadContainer(() => import("./pages/reporting/reporting.page"));
const SellThroughReportingPage = DynamicLoadContainer(() => import("./pages/reporting/sell-through/sell-through.page"));
const SummaryOrdersPage = DynamicLoadContainer(() => import("./pages/smart-orders/summary-orders.page"));
const SmartOrderDetailsPage = DynamicLoadContainer(
    () => import("./pages/smart-orders/smart-order-details/smart-order-details.page")
);
const LowInventoryReportingPage = DynamicLoadContainer(
    () => import("./pages/reporting/low-inventory/low-inventory.page")
);
//import BrandContainer from "./containers/brand";
const LandingPageContainer = DynamicLoadContainer(() => import("./containers/dashboard/landing-page"));
const BrandDashboardContainer = DynamicLoadContainer(() => import("./containers/dashboard/dashboard"));
const BuyerDashboardContainer = DynamicLoadContainer(() => import("./containers/buyer-dashboard/dashboard"));
const BrandsContainer = DynamicLoadContainer(() => import("./containers/brands"));
const CampaignContainer = DynamicLoadContainer(() => import("./containers/campaign"));
const CampaignsContainer = DynamicLoadContainer(() => import("./containers/campaigns"));

const CatalogContainer = DynamicLoadContainer(() => import("./containers/catalog/catalog"));
const CreateCatalogContainer = DynamicLoadContainer(() => import("./containers/catalog/create"));
const CatalogEntriesContainer = DynamicLoadContainer(() => import("./containers/catalog/entries"));
const CartContainer = DynamicLoadContainer(() => import("./containers/cart"));
const GalleryContainer = DynamicLoadContainer(() => import("./containers/gallery"));
const ProductContainer = DynamicLoadContainer(() => import("./containers/product"));
const ItemPreviewContainer = DynamicLoadContainer(() => import("./containers/item-preview-container"));

const WhiteboardContainer = DynamicLoadContainer(() => import("./containers/whiteboard"));
const WhiteboardsContainer = DynamicLoadContainer(() => import("./containers/whiteboards"));

const LinesheetsContainerOld = DynamicLoadContainer(() => import("./containers/linesheets-old"));
const LinesheetsContainer = DynamicLoadContainer(() => import("./containers/linesheets"));
const ListsComponent = DynamicLoadContainer(() => import("./containers/lists"));
const LoginContainer = DynamicLoadContainer(() => import("./containers/login"));
const SignupContainer = DynamicLoadContainer(() => import("./containers/signup"));
const OrderContainer = DynamicLoadContainer(() => import("./containers/order"));
const SummaryContainer = DynamicLoadContainer(() => import("./containers/order-summary"));
const OrdersContainer = DynamicLoadContainer(() => import("./containers/orders"));
const OrderIntentContainer = DynamicLoadContainer(() => import("./containers/order-intent"));
const PageViewsContainer = DynamicLoadContainer(() => import("./containers/page-views"));
const SizePacksUploadContainer = DynamicLoadContainer(() => import("./containers/size-packs-upload"));
const ProfileContainer = DynamicLoadContainer(() => import("./containers/profile"));
const SmartGroupContainer = DynamicLoadContainer(() => import("./containers/smart-group"));
const SmartGroupsContainer = DynamicLoadContainer(() => import("./containers/smart-groups"));
const StaticGroupContainer = DynamicLoadContainer(() => import("./containers/static-group"));
const DownloadPage = DynamicLoadContainer(() => import("./containers/legacy-download"));
const InventoryAndOrderingContainer = DynamicLoadContainer(() => import("./containers/product/inventory-and-ordering"));
const B2BMerchantEnablement = DynamicLoadContainer(
    () => import("./containers/b2b-merchant-enablement/b2b-merchant-enablement")
);
const B2BMerchantEnablementWrapper = DynamicLoadContainer(
    () => import("./containers/b2b-merchant-enablement/components/merchant-enablement-wrapper")
);
const B2BIntegrationSettings = DynamicLoadContainer(
    () => import("./containers/b2b-merchant-enablement/lightspeed-retail-integration-settings-page")
);
const B2BMerchantManagement = DynamicLoadContainer(
    () => import("./containers/b2b-merchant-management/b2b-merchant-management")
);

const MerchantSettingsTemplate = DynamicLoadContainer(
    () => import("./pages/merchant-settings/merchant-settings.template")
);
const MerchantSettingsGeneralPage = DynamicLoadContainer(
    () => import("./pages/merchant-settings/general-page/general.page")
);
const MerchantSettingsStoreProfilePage = DynamicLoadContainer(
    () => import("./pages/merchant-settings/store-profile-page/store-profile.page")
);

import ProductCollectionPageContainer from "./containers/product-collections/product-collection-page";
import redirectToRollupMode from "middleware/redirect-to-rollup-mode";
import redirectToDrive from "middleware/redirect-to-drive";
import redirectToMerchantNotFound from "middleware/redirect-to-deactivated-merchant";
import LegacyCartShipmentReviewContainer from "containers/legacy-cart/shipment-review-root-container";
import lsidIdentity from "middleware/lsid-identity";
const CollectionHomeContainer = DynamicShimmerLoader(
    () => import("./containers/product-collections/product-collection-home")
);
const CollectionShopContainer = DynamicShimmerLoader(
    () => import("./containers/product-collections/product-collection-shop")
);
const CollectionShopHomeContainer = DynamicShimmerLoader(
    () => import("./containers/product-collections/product-collection-shop-home")
);
const CollectionShopBrandContainer = DynamicShimmerLoader(
    () => import("./containers/product-collections/product-collection-shop-brand")
);
const CollectionShopBrandHomeContainer = DynamicShimmerLoader(
    () => import("./containers/product-collections/product-collection-shop-brand-home")
);
const CollectionShopSearchContainer = DynamicShimmerLoader(
    () => import("./containers/product-collections/product-collection-shop-search")
);
const CollectionShopBrandCatalogContainer = DynamicShimmerLoader(
    () => import("./containers/product-collections/product-collection-shop-brand-catalog")
);
const CollectionCartContainer = DynamicShimmerLoader(
    () => import("./containers/product-collections/product-collection-cart")
);

const MerchantAdminContainer = DynamicShimmerLoader(() => import("./containers/merchant-app/containers/merchant-app"));
const MerchantAdminAccountContainer = DynamicShimmerLoader(
    () => import("./containers/merchants/account/merchant-admin-account")
);
const MerchantAdminMemberListContainer = DynamicShimmerLoader(
    () => import("./containers/merchants/member-list/merchant-admin-member-list")
);
const MerchantAdminMemberDetailsContainer = DynamicShimmerLoader(
    () => import("./containers/merchants/member-details/merchant-admin-member-details")
);
const MerchantSettingsContainer = DynamicShimmerLoader(
    () => import("./containers/product-collections/merchant-settings")
);
const MerchantSettingsItemSchemaContainer = DynamicShimmerLoader(
    () => import("./containers/product-collections/merchant-settings-item-schema")
);
const MerchantSettingsPreferenceContainer = DynamicShimmerLoader(
    () => import("./containers/product-collections/merchant-settings-preference")
);
const MerchantSettingsDefaultAllocationDimensionsPreferenceContainer = DynamicShimmerLoader(
    () => import("./containers/product-collections/merchant-settings-default-allocation-dimensions-preference")
);
const MerchantSettingsAllocationDimensionContainer = DynamicShimmerLoader(
    () => import("./containers/product-collections/merchant-settings-allocation-dimension")
);
const MerchantSettingsAllocationDimensionCreateContainer = DynamicShimmerLoader(
    () => import("./containers/product-collections/merchant-settings-allocation-dimension-create")
);
const MerchantSettingsAllocationDimensionEditContainer = DynamicShimmerLoader(
    () => import("./containers/product-collections/merchant-settings-allocation-dimension-edit")
);
const MerchantSettingsAllocationDimensionValues = DynamicShimmerLoader(
    () => import("./containers/product-collections/merchant-settings-allocation-dimension-values")
);
const MerchantSettingsPurchaseOrderDoorGroupContainer = DynamicShimmerLoader(
    () => import("./containers/product-collections/merchant-settings-purchase-order-door-group")
);
const MerchantSettingsVersionStatusContainer = DynamicShimmerLoader(
    () => import("./containers/product-collections/merchant-settings-version-status")
);
const MerchantSettingsDefaultRowContainer = DynamicShimmerLoader(
    () => import("./containers/product-collections/merchant-settings-default-row-setting")
);
const MerchantSettingsDefaultSharedSettings = DynamicShimmerLoader(
    () => import("./containers/product-collections/merchant-settings-default-shared-settings")
);
const MerchantSettingsFeaturePreferenceContainer = DynamicShimmerLoader(
    () => import("./containers/product-collections/merchant-settings-feature-preference")
);

const InstagramRedirectPage = DynamicLoadContainer(() => import("./containers/instagram-redirect"));
const CustomTemplates = DynamicLoadContainer(() => import("containers/admin/custom-templates"));
const TemplatePage = DynamicLoadContainer(() => import("containers/admin/template-page"));
const AtsCustomTemplateList = DynamicLoadContainer(
    () => import("containers/admin/settings/ats-custom-template-list-page")
);
const AtsCustomTemplate = DynamicLoadContainer(() => import("containers/admin/settings/ats-custom-template-page"));

const ContactsPage = DynamicLoadContainer(() => import("containers/contacts/contacts-container"));
const CompanyPage = DynamicLoadContainer(() => import("containers/contacts/company-container"));
const BuyerPage = DynamicLoadContainer(() => import("containers/contacts/buyer-container"));

const ConnectionRequestsPage = DynamicLoadContainer(() => import("containers/connection-requests-page"));
const RequestImpersonationContainer = DynamicLoadContainer(() => import("./containers/request-impersonation"));
const StopImpersonationContainer = DynamicLoadContainer(() => import("./containers/stop-impersonation"));
const EnterImpersonationContainer = DynamicLoadContainer(() => import("./containers/enter-impersonation"));

const CancelledStylesOrdersPage = DynamicLoadContainer(
    () => import("./containers/cancelled-styles/cancelled-styles-orders-page")
);
const CancelledStylesPage = DynamicLoadContainer(() => import("./containers/cancelled-styles/cancelled-styles-page"));
const CancelledStylePage = DynamicLoadContainer(() => import("./containers/cancelled-styles/cancelled-style-page"));
const CancelledStylesOrderPage = DynamicLoadContainer(
    () => import("./containers/cancelled-styles/cancelled-styles-order-page")
);

//Payments module federation pages
const PaymentHistory = DynamicShimmerLoader(() => import("./containers/payments/payment-history-container"));
const PaymenConfigurationsSettingsContainer = DynamicLoadContainer(
    () => import("./containers/admin/payments/payment-configurations-container.tsx")
);
const PaymentPreferencesSettingsContainer = DynamicLoadContainer(
    () => import("./containers/admin/payments/payment-preferences-container")
);
const PaymentProcessingContainer = DynamicLoadContainer(
    () => import("./containers/admin/payments/manage-payment-processing-container")
);
const CheckoutContainer = DynamicLoadContainer(() => import("./containers/payments/checkout-container"));
const MobilePaymentsWrapperContainer = DynamicLoadContainer(
    () => import("./containers/payments/mobile-payments-wrapper-container")
);
const CheckoutChallengeContainer = DynamicLoadContainer(() => import("./containers/checkout/challenge-container.tsx"));
const ManagePaymentMethodsContainer = DynamicLoadContainer(
    () => import("./containers/manage-payments-methods-container.tsx")
);
const LightspeedReports = DynamicShimmerLoader(() => import("./containers/payments/lightspeed-reports-container.tsx"));
const InvoicesContainer = DynamicShimmerLoader(() => import("./containers/payments/invoices-container.tsx"));

//Admin Pages
const AdminIndexContainer = DynamicLoadContainer(() => import("./containers/admin/index"));
const AdminLegacyLocalesIndexContainer = DynamicLoadContainer(() => import("./containers/admin/index-legacy-locales"));
const PricingContainer = DynamicLoadContainer(() => import("./containers/admin/settings/pricing"));
const ShippingManagerContainer = DynamicLoadContainer(() => import("./containers/admin/settings/manage-shipping"));
const ShippingAccountCarrierContainer = DynamicLoadContainer(
    () => import("./containers/admin/settings/shipping-account-carrier")
);
const PromotionsContainer = DynamicLoadContainer(() => import("./containers/admin/settings/promotions"));
const PromotionContainer = DynamicLoadContainer(() => import("./containers/admin/settings/promotion"));
const InventorySettingsContainer = DynamicLoadContainer(() => import("./containers/admin/settings/inventory-settings"));
const PreferredRetailersContainer = DynamicLoadContainer(() => import("./containers/management/preferred-retailers"));
const OrderSettingsContainer = DynamicLoadContainer(() => import("./containers/admin/settings/order-settings"));
const EmailImageSettingsContainer = DynamicLoadContainer(
    () => import("./containers/admin/settings/email-image-settings")
);
const OrderMinMaxSettingsContainer = DynamicLoadContainer(() => import("./containers/admin/settings/order-min-max"));
const BarcodeSettingsContainer = DynamicLoadContainer(() => import("./containers/admin/settings/barcode-management"));
const ImageReportsContainer = DynamicLoadContainer(() => import("./containers/admin/media/image-reports"));
const BrandSettingsContainer = DynamicLoadContainer(() => import("./containers/admin/settings/brand-settings"));

const ManageDataSyncContainer = DynamicLoadContainer(
    () => import("./containers/admin/settings/manage-data-sync/manage-data-sync")
);
const ManageEditMappingContainer = DynamicLoadContainer(
    () => import("./containers/admin/settings/manage-data-sync/edit-mapping/edit-mapping")
);
const CustomerUserGroupsContainer = DynamicLoadContainer(
    () => import("./containers/admin/settings/customer-user-groups")
);

const CustomerSizeFamiliesContainer = DynamicLoadContainer(
    () => import("./containers/admin/settings/customer-size-families")
);

const OnlineTradeShowContainer = DynamicLoadContainer(() => import("./containers/admin/settings/online-trade-show"));
const MatterportIntegrationContainer = DynamicLoadContainer(
    () => import("./containers/admin/settings/matterport-integration")
);
const InventoryDataPageContainer = DynamicLoadContainer(() => import("./containers/admin/data/inventory-data-page"));
const CompanyDataPageContainer = DynamicLoadContainer(() => import("./containers/admin/data/company-data-page"));
const BuyerDataPageContainer = DynamicLoadContainer(() => import("./containers/admin/data/buyer-data-page"));
const ProductDataPageContainer = DynamicLoadContainer(() => import("./containers/admin/data/product-data-page"));
const PriceSheetsPageContainer = DynamicLoadContainer(() => import("./containers/admin/data/price-sheets-page"));
const SizeManagerContainer = DynamicLoadContainer(() => import("./containers/admin/settings/manage-sizes"));
const ColorSwatchesPage = DynamicLoadContainer(() => import("./containers/admin/media/color-swatches-page"));
const ProductImagesContainer = DynamicLoadContainer(() => import("./containers/admin/media/product-images-page"));
const ManageLogoContainer = DynamicLoadContainer(
    () => import("./containers/admin/customizations/manage-logo/manage-logo-page")
);

const ImageSettingsContainer = DynamicLoadContainer(
    () => import("./containers/admin/media/image-settings/image-settings-page")
);
const TermsAndConditionsContainer = DynamicLoadContainer(
    () => import("./containers/admin/settings/terms-and-conditions")
);
const MerchantAdminSettingsContainer = DynamicLoadContainer(
    () => import("./containers/admin/settings/merchant-admin-settings")
);
const ManageVADataContainer = DynamicLoadContainer(
    () => import("./containers/admin/settings/manage-va-data/manage-va-data")
);
const ManageAllocationDimensionsContainer = DynamicLoadContainer(
    () => import("./containers/admin/settings/manage-va-data/manage-allocation-dimensions")
);
const ManageSizeCurveImportContainer = DynamicLoadContainer(
    () => import("./containers/admin/settings/manage-va-data/manage-size-curve-import")
);
const AssortmentSchemaDataContainer = DynamicLoadContainer(
    () => import("./containers/assortments-for-brands/assortment-schema-data")
);
const MobileTermsAndConditions = DynamicLoadContainer(() => import("./containers/mobile-terms-and-conditions"));
const SizePacksContainer = DynamicLoadContainer(() => import("./containers/admin/data/size-packs-page"));
const WatermarkingContainer = DynamicLoadContainer(
    () => import("containers/admin/media/watermarking/watermark-settings")
);
const PermissionsManagementSettings = DynamicLoadContainer(
    () => import("containers/admin/permissions/permissions-management.tsx")
);

//Management Pages
const MgmtIndexContainer = DynamicLoadContainer(() => import("./containers/management"));
const FolderContainer = DynamicLoadContainer(() => import("./containers/folders"));
const MerchantsContainer = DynamicLoadContainer(() => import("./containers/folders/merchants"));
const MgmtUtilitiesContainer = DynamicLoadContainer(() => import("./containers/management/utilities"));
const MgmtUtilitiesManageUsersContainer = DynamicLoadContainer(
    () => import("containers/management/utilities/manage-users")
);
const MgmtUtilitiesDisableUsersContainer = DynamicLoadContainer(
    () => import("containers/management/utilities/sop-disable-users")
);
const DataflowManagementContainer = DynamicLoadContainer(() => import("containers/management/utilities/dataflow"));
const UserInformationLookupContainer = DynamicLoadContainer(
    () => import("containers/management/utilities/user-lookup")
);
const ActiveStatusLookupContainer = DynamicLoadContainer(
    () => import("containers/management/utilities/active-status-lookup")
);

// const BuyerDashboard = DynamicLoadContainer(() => import("containers/buyer-dashboard/dashboard"));
const MerchantInventorySettingsContainer = DynamicLoadContainer(
    () => import("containers/management/merchant-inventory-settings")
);

const CustomizationSettingsContainer = DynamicLoadContainer(
    () => import("containers/management/customization-settings")
);

const InternalNotes = DynamicLoadContainer(() => import("containers/management/internal-notes"));
const SchemaMapper = DynamicLoadContainer(() => import("containers/management/schema-mapper"));
const LifecycleScripts = DynamicLoadContainer(() => import("containers/management/lifecycle-scripts"));
const ImagesManagementContainer = DynamicLoadContainer(() => import("containers/management/images-management-page"));
const AssortmentsManagement = DynamicLoadContainer(
    () => import("containers/management/utilities/assortments-management")
);
const AssortmentsManagementSuperusers = DynamicLoadContainer(
    () => import("containers/management/utilities/assortments-management-superusers")
);
const MerchantsManagement = DynamicLoadContainer(() => import("containers/management/utilities/merchants-management"));
const AssortmentsSupport = DynamicLoadContainer(() => import("containers/management/utilities/assortment-support"));
const ProductSizesOverridesContainer = DynamicLoadContainer(
    () => import("containers/management/utilities/product-sizes-overrides")
);
const SitesManagerContainer = DynamicLoadContainer(() => import("containers/sites-manager/sites-manager"));
const SiteDetailsContainer = DynamicLoadContainer(() => import("containers/sites-manager/site-details"));
const PageDetailsContainer = DynamicLoadContainer(() => import("containers/sites-manager/page-details"));
const TemplatePagesContainer = DynamicLoadContainer(() => import("containers/sites-manager/template-pages"));
const PageLayoutContainer = DynamicLoadContainer(() => import("containers/sites-manager/live-pages/page-layout"));
const LivePageDetailsContainer = DynamicLoadContainer(
    () => import("containers/sites-manager/live-pages/live-page-details")
);
const LiveBrandSitePageDetailsContainer = DynamicLoadContainer(
    () => import("containers/sites-manager/live-pages/live-brand-site-page-details")
);
const GlobalSearchContainer = DynamicLoadContainer(() => import("containers/sites-manager/live-pages/global-search"));
const NavSites = DynamicLoadContainer(() => import("containers/management/utilities/nav-sites"));

//Misc Pages
const PasswordResetInvalid = DynamicLoadContainer(() => import("./containers/expired-forgot-password"));
const PasswordReset = DynamicLoadContainer(() => import("./containers/password-reset"));
const ForgotPassword = DynamicLoadContainer(() => import("./containers/forgot-password"));
const BulkOrderPDfPage = DynamicLoadContainer(() => import("./containers/bulk-order-pdf-download"));
const LoginSSOContainer = DynamicLoadContainer(() => import("./containers/login-sso/login-sso"));

const ProductCollectionDatalossContainer = DynamicShimmerLoader(
    () => import("./containers/dataloss/product-collection-dataloss")
);

// promotions
const ManagePromotionsContainer = DynamicLoadContainer(() => import("./containers/admin/promotions/promotions-page"));
const ManagePromotionsListContainer = DynamicLoadContainer(
    () => import("./containers/admin/promotions/promotions-list")
);

const ProductVisibilityReport = DynamicLoadContainer(() => import("./containers/product-visibility-report/index"));
const InviteToMerchantOrganizationErrorPage = DynamicLoadContainer(
    () => import("./containers/invite-to-merchant-organization-error-page")
);

import { RetailerProfilePage } from "pages/retailer-profile";
import { ROUTES } from "pages/routes.constants";

export default (flags) => {
    return (
        <Route path="/" component={App} onEnter={session}>
            <IndexRoute component={LoginContainer} onEnter={redirectIfLoggedIn()} />
            <Route path="error-page" component={InviteToMerchantOrganizationErrorPage} />
            <Route onEnter={chainMiddleware([requireAuth, sanitizeUrl, lsidIdentity])} onChange={onRouteChanged}>
                <Route
                    path="/profile"
                    subdomain="profile"
                    uri="profile"
                    component={ProfileContainer}
                    onEnter={requireFullSignIn}
                />
                <Route
                    path="/request-impersonation"
                    component={RequestImpersonationContainer}
                    onEnter={chainMiddleware([checkUserType("not-nuorder"), requireFullSignIn, redirectIfTempPassword])}
                />
                <Route
                    path="/stop-impersonation"
                    component={StopImpersonationContainer}
                    onEnter={chainMiddleware([requireFullSignIn, checkUserType("nuorder")])}
                />
                <Route
                    path="/enter-impersonation"
                    component={EnterImpersonationContainer}
                    onEnter={chainMiddleware([requireFullSignIn, checkUserType("nuorder"), redirectIfTempPassword])}
                />
                <Route onEnter={redirectIfTempPassword}>
                    <Route
                        path="/easyorder/:timestamp/:ezid"
                        subdomain="wholesale"
                        uri="/easyorder/:timestamp/:ezid"
                        component={LegacyContainer}
                    />
                    <Redirect from="browse/brands" to="site/nuordermarketplace" />

                    <Route
                        path="site/:slug"
                        title="Site"
                        subdomain="sites"
                        uri="nuorder"
                        component={PageLayoutContainer}
                        onEnter={chainMiddleware([
                            requireFullSignIn,
                            checkRetailerProfile(flags),
                            requestedConnections,
                        ])}
                    >
                        <IndexRoute component={LivePageDetailsContainer} />
                        <Route
                            path="page/:pageId"
                            title="Site"
                            subdomain="sites"
                            uri="nuorder"
                            component={LivePageDetailsContainer}
                            onEnter={chainMiddleware([requireFullSignIn, checkRetailerProfile(flags)])}
                        />
                        <Route
                            path="search"
                            title="Global Search"
                            subdomain="sites"
                            uri="nuorder"
                            component={GlobalSearchContainer}
                            onEnter={chainMiddleware([requireFullSignIn, checkRetailerProfile(flags)])}
                        />
                    </Route>

                    <Route
                        path={ROUTES.RETAILER_PROFILE}
                        component={() => (
                            <RetailerProfilePage isRedesignedPage={flags.enableRedesignedRetailerProfilePageNu61440} />
                        )}
                        onEnter={chainMiddleware([checkUserType("buyer"), removeActiveBrand, removeActiveCompany])}
                    />

                    <Route
                        path="drive"
                        title="Drive"
                        component={FolderContainer}
                        onEnter={chainMiddleware([
                            checkUserType("access-collections"),
                            requireFullSignIn,
                            redirectToRetail(flags.retailCodebaseSplit),
                            redirectToMerchantNotFound,
                        ])}
                    />

                    <Route
                        path="merchants"
                        title="Merchants"
                        component={MerchantsContainer}
                        onEnter={chainMiddleware([requireFullSignIn, redirectToRetail(flags.retailCodebaseSplit)])}
                    />

                    <Route
                        path="merchant/:id/settings"
                        component={MerchantSettingsContainer}
                        onEnter={chainMiddleware([
                            checkUserType("access-merchants"),
                            requireFullSignIn,
                            redirectToRetail(flags.retailCodebaseSplit),
                        ])}
                    >
                        <IndexRoute
                            parentSection="wholesale"
                            wholesaleVisible={false}
                            component={MerchantSettingsItemSchemaContainer}
                        />
                        <Route path="schema" wholesaleVisible={false} component={MerchantSettingsItemSchemaContainer} />
                        <Route
                            path="preference"
                            wholesaleVisible={false}
                            component={MerchantSettingsPreferenceContainer}
                        />
                        <Route
                            path="default-door-preference"
                            wholesaleVisible={false}
                            component={MerchantSettingsDefaultAllocationDimensionsPreferenceContainer}
                        />
                        <Route
                            path="allocation-dimension"
                            wholesaleVisible={false}
                            component={MerchantSettingsAllocationDimensionContainer}
                        >
                            <Route
                                path="create"
                                wholesaleVisible={false}
                                component={MerchantSettingsAllocationDimensionCreateContainer}
                            />
                            <Route
                                path="edit/:allocationDimensionId"
                                wholesaleVisible={false}
                                component={MerchantSettingsAllocationDimensionEditContainer}
                            />
                            <Route
                                path="values/:allocationDimensionId"
                                wholesaleVisible={false}
                                component={MerchantSettingsAllocationDimensionValues}
                            />
                        </Route>
                        <Route
                            path="purchase-order-door-group"
                            wholesaleVisible={false}
                            component={MerchantSettingsPurchaseOrderDoorGroupContainer}
                        />
                        <Route
                            path="version-status"
                            wholesaleVisible={false}
                            component={MerchantSettingsVersionStatusContainer}
                        />
                        <Route
                            path="default-row"
                            wholesaleVisible={false}
                            component={MerchantSettingsDefaultRowContainer}
                        />
                        <Route
                            path="shared-settings"
                            wholesaleVisible={false}
                            component={MerchantSettingsDefaultSharedSettings}
                        />
                        <Route
                            path="feature-preference"
                            wholesaleVisible={false}
                            component={MerchantSettingsFeaturePreferenceContainer}
                        />
                    </Route>

                    <Route
                        path="merchant/admin"
                        component={MerchantAdminContainer}
                        onEnter={redirectToRetail(flags.retailCodebaseSplit)}
                    >
                        <IndexRoute component={MerchantAdminAccountContainer} />
                        <Route path="members" component={MerchantAdminMemberListContainer} />
                        <Route path="members/:memberId" component={MerchantAdminMemberDetailsContainer} />
                        <Route
                            path="merchant-settings"
                            component={MerchantAdminSettingsContainer}
                            wholesaleVisible={false}
                            onEnter={chainMiddleware([
                                requireBrand("/merchant/admin"),
                                requireFullSignIn,
                                hasPermission("vertical_assortment_access"),
                            ])}
                            onChange={chainOnChangeMiddeware([
                                wrapOnChange(requireBrand("/merchant/admin")),
                                wrapOnChange(hasPermission("vertical_assortment_access")),
                            ])}
                        >
                            <IndexRoute component={ManageVADataContainer} />
                            <Route path="schema" component={AssortmentSchemaDataContainer} />
                            <Route path="members" component={MerchantAdminMemberListContainer} />
                            <Route path="size-curve-import" component={ManageSizeCurveImportContainer} />
                            <Route path=":dimensionType" component={ManageAllocationDimensionsContainer} />
                        </Route>
                        <Redirect from="*" to="/merchant/admin" />
                    </Route>

                    <Route path="/assets/download-external/:uploadId" component={DownloadPage} />

                    <Route
                        path="assortment/:id"
                        component={ProductCollectionPageContainer}
                        onEnter={chainMiddleware([
                            checkUserType("access-collections"),
                            requireFullSignIn,
                            redirectToRollupMode,
                            redirectToRetail(flags.retailCodebaseSplit),
                        ])}
                    >
                        <Route
                            path=""
                            parentSection="wholesale"
                            wholesaleVisible={false}
                            component={CollectionCartContainer}
                        >
                            <IndexRoute
                                parentSection="wholesale"
                                wholesaleVisible={false}
                                component={CollectionHomeContainer}
                            />
                            <Route path="shop" wholesaleVisible={false} component={CollectionShopContainer}>
                                {/* Brands page just forwards you on the shope/:last_brand for now */}
                                <IndexRoute
                                    parentSection="wholesale"
                                    wholesaleVisible={false}
                                    component={CollectionShopHomeContainer}
                                    onEnter={redirectBrandUser}
                                />
                                <Route
                                    path="brand/:brand"
                                    wholesaleVisible={false}
                                    component={CollectionShopBrandContainer}
                                    onEnter={chainMiddleware([
                                        setActiveBrandFromCache,
                                        requireNonBlockedUser,
                                        activeCompany,
                                    ])}
                                >
                                    <IndexRoute
                                        parentSection="wholesale"
                                        wholesaleVisible={false}
                                        component={CollectionShopBrandHomeContainer}
                                    />
                                    <Route
                                        path="search"
                                        wholesaleVisible={false}
                                        component={CollectionShopSearchContainer}
                                    />
                                    <Route
                                        path="catalog/:catalog"
                                        wholesaleVisible={false}
                                        component={CollectionShopBrandCatalogContainer}
                                    />
                                </Route>
                                <Route
                                    path="search"
                                    wholesaleVisible={false}
                                    component={CollectionShopSearchContainer}
                                    onEnter={redirectBrandUser}
                                />
                            </Route>
                        </Route>
                    </Route>

                    <Route
                        path="assortment/:id/revisions"
                        component={ProductCollectionDatalossContainer}
                        onEnter={chainMiddleware([
                            checkUserType("access-collections"),
                            requireFullSignIn,
                            redirectToRetail(flags.retailCodebaseSplit),
                        ])}
                    >
                        <IndexRoute
                            parentSection="wholesale"
                            wholesaleVisible={false}
                            component={CollectionCartContainer}
                        />
                    </Route>

                    <Redirect from="discover" to="browse/brands" />

                    <Route onEnter={chainMiddleware([setActiveBrandFallback, sanitizeUrl, requestedConnections])}>
                        <Route
                            path="dashboard"
                            component={BuyerDashboardContainer}
                            onEnter={chainMiddleware([checkUserType("buyer-only"), activeCompany])}
                        />
                        <Route
                            path="brands"
                            component={BrandsContainer}
                            onEnter={chainMiddleware([requireFullSignIn, redirectToLastBrand])}
                        />
                        <Route
                            path="brand/:brand"
                            onEnter={chainMiddleware([
                                setActiveBrandFromUrl,
                                requireNonBlockedUser,
                                activeCompany,
                                checkRetailerProfile(flags),
                            ])}
                        >
                            <Route onEnter={activeCompany}>
                                <Route component={LegacyWholesale} onEnter={canAccessModule("wholesale")}>
                                    {/*<IndexRoute component={BrandContainer} />*/}
                                    <Route
                                        path="cart"
                                        parentSection="wholesale"
                                        wholesaleVisible={false}
                                        component={CartContainer}
                                    />
                                    <Route
                                        path="cart/checkout"
                                        parentSection="wholesale"
                                        wholesaleVisible={false}
                                        component={CartContainer}
                                    />
                                    <Route
                                        path="cart/checkout/confirmation/:order_group_id"
                                        parentSection="wholesale"
                                        wholesaleVisible={false}
                                        component={CartContainer}
                                    />
                                    <Route
                                        path="cart/reorder"
                                        parentSection="wholesale"
                                        wholesaleVisible={false}
                                        component={CartContainer}
                                    />
                                    <IndexRoute
                                        parentSection="wholesale"
                                        wholesaleVisible={false}
                                        component={LandingPageContainer}
                                    />
                                    <Route
                                        path="gallery"
                                        parentSection="wholesale"
                                        wholesaleVisible={false}
                                        component={GalleryContainer}
                                    >
                                        <Route
                                            path="item/:item"
                                            wholesaleVisible={false}
                                            component={ItemPreviewContainer}
                                            onLeave={warning.onLeave}
                                        >
                                            <IndexRoute
                                                parentSection="wholesale"
                                                wholesaleVisible={false}
                                                component={InventoryAndOrderingContainer}
                                            />
                                        </Route>
                                    </Route>
                                    <Route
                                        path="linesheets"
                                        parentSection="wholesale"
                                        wholesaleVisible={false}
                                        component={
                                            // TODO: [NU-71187] Clean with linesheetsGalleryUiEnhancementsNU69363 FF
                                            flags.linesheetsGalleryUiEnhancementsNu69363
                                                ? LinesheetsContainer
                                                : LinesheetsContainerOld
                                        }
                                    >
                                        <Route
                                            path="item/:item"
                                            wholesaleVisible={false}
                                            component={ItemPreviewContainer}
                                            onLeave={warning.onLeave}
                                        >
                                            <IndexRoute
                                                parentSection="wholesale"
                                                wholesaleVisible={false}
                                                component={InventoryAndOrderingContainer}
                                            />
                                        </Route>
                                    </Route>
                                    <Route
                                        path="linesheets/create"
                                        parentSection="wholesale"
                                        wholesaleVisible={false}
                                        component={CreateCatalogContainer}
                                    />
                                    <Route
                                        path="linesheet/:catalog"
                                        parentSection="wholesale"
                                        wholesaleVisible={false}
                                        component={CatalogContainer}
                                    >
                                        <IndexRoute
                                            parentSection="wholesale"
                                            wholesaleVisible={false}
                                            component={CatalogEntriesContainer}
                                        />
                                        <Route
                                            parentSection="wholesale"
                                            wholesaleVisible={false}
                                            component={CatalogEntriesContainer}
                                        >
                                            <Route
                                                path="item/:item"
                                                wholesaleVisible={false}
                                                component={ItemPreviewContainer}
                                                onLeave={warning.onLeave}
                                            >
                                                <IndexRoute
                                                    parentSection="wholesale"
                                                    wholesaleVisible={false}
                                                    component={InventoryAndOrderingContainer}
                                                />
                                            </Route>
                                        </Route>
                                    </Route>
                                    <Route
                                        path="list/:catalog"
                                        parentSection="wholesale"
                                        wholesaleVisible={false}
                                        component={CatalogContainer}
                                    >
                                        <IndexRoute
                                            parentSection="wholesale"
                                            wholesaleVisible={false}
                                            component={CatalogEntriesContainer}
                                        />
                                        <Route
                                            parentSection="wholesale"
                                            wholesaleVisible={false}
                                            component={CatalogEntriesContainer}
                                        >
                                            <Route
                                                path="item/:item"
                                                wholesaleVisible={false}
                                                component={ItemPreviewContainer}
                                                onLeave={warning.onLeave}
                                            >
                                                <IndexRoute
                                                    parentSection="wholesale"
                                                    wholesaleVisible={false}
                                                    component={InventoryAndOrderingContainer}
                                                />
                                            </Route>
                                        </Route>
                                    </Route>

                                    <Route
                                        path="lists"
                                        parentSection="wholesale"
                                        wholesaleVisible={false}
                                        component={ListsComponent}
                                    />
                                    <Route
                                        path="item/:item"
                                        parentSection="wholesale"
                                        wholesaleVisible={false}
                                        component={ProductContainer}
                                    >
                                        <IndexRoute
                                            parentSection="wholesale"
                                            wholesaleVisible={false}
                                            component={InventoryAndOrderingContainer}
                                        />
                                    </Route>
                                    <Route
                                        path="whiteboards"
                                        wholesaleVisible={false}
                                        component={WhiteboardsContainer}
                                        onEnter={chainMiddleware([
                                            hasPermission("can_create_whiteboards"),
                                            redirectToDrive,
                                        ])}
                                    />
                                    <Route
                                        path="whiteboard/:id"
                                        wholesaleVisible={false}
                                        component={WhiteboardContainer}
                                        onEnter={hasPermission("can_create_whiteboards")}
                                    >
                                        <Route
                                            path="page/:pageId"
                                            wholesaleVisible={false}
                                            component={WhiteboardContainer}
                                            onEnter={hasPermission("can_create_whiteboards")}
                                        />
                                    </Route>

                                    <Route
                                        path="inventory/export/:id"
                                        wholesaleVisible={false}
                                        component={InventoryDownloadContainer}
                                    />
                                    <Route
                                        path="items/export/:id"
                                        wholesaleVisible={false}
                                        component={ItemsDownloadContainer}
                                    />
                                    <Route
                                        path="orders/export/:id"
                                        onEnter={requireFullSignIn}
                                        wholesaleVisible={false}
                                        component={OrderDownloadContainer}
                                    />
                                    <Route
                                        path="ats/export/:id"
                                        onEnter={requireFullSignIn}
                                        wholesaleVisible={false}
                                        component={AtsDownloadContainer}
                                    />
                                    <Route
                                        path="media"
                                        parentSection="wholesale"
                                        wholesaleVisible={true}
                                        component={BlankContainer}
                                        onEnter={checkUserType("not-guest-buyer")}
                                    />
                                    <Route
                                        path="contacts"
                                        wholesaleVisible={true}
                                        component={ContactsPage}
                                        onEnter={checkUserType("not-guest-buyer")}
                                    />
                                    <Route
                                        path="contacts/company/:id"
                                        wholesaleVisible={false}
                                        featureIsDisabled={true}
                                        component={CompanyPage}
                                        onEnter={checkUserType("not-guest-buyer")}
                                    />
                                    <Route
                                        path="contacts/buyer/:id"
                                        wholesaleVisible={false}
                                        featureIsDisabled={true}
                                        component={BuyerPage}
                                        onEnter={checkUserType("not-guest-buyer")}
                                    />
                                    <Route
                                        path="connection-requests"
                                        parentSection="wholesale"
                                        wholesaleVisible={false}
                                        component={ConnectionRequestsPage}
                                        onEnter={chainMiddleware([
                                            checkUserType("brand"),
                                            hasPermission("can_manage_connection_requests"),
                                            requireFullSignIn,
                                        ])}
                                    />
                                    <Route
                                        path="orders"
                                        parentSection="wholesale"
                                        wholesaleVisible={false}
                                        component={OrdersContainer}
                                        onEnter={chainMiddleware([checkUserType("not-guest-buyer"), requireFullSignIn])}
                                    />
                                    <Route
                                        path="order/:order"
                                        wholesaleVisible={false}
                                        component={OrderContainer}
                                        onEnter={chainMiddleware([checkUserType("not-guest-buyer"), requireFullSignIn])}
                                    />
                                    <Route
                                        path="order/:order/summary"
                                        wholesaleVisible={false}
                                        component={SummaryContainer}
                                        hideHeader={true}
                                        onEnter={checkUserType("not-guest-buyer")}
                                    />
                                    <Route
                                        path="order-intent/:id"
                                        wholesaleVisible={false}
                                        component={OrderIntentContainer}
                                        onEnter={chainMiddleware([checkUserType("not-guest-buyer"), requireFullSignIn])}
                                    />
                                    <Route
                                        path="cancelled-styles-orders"
                                        parentSection="wholesale"
                                        component={CancelledStylesOrdersPage}
                                        wholesaleVisible={false}
                                        onEnter={chainMiddleware([
                                            requireFullSignIn,
                                            hasPermission("manage_cancelled_styles"),
                                            checkPreference("cancelled_styles"),
                                        ])}
                                    />
                                    <Route
                                        path="cancelled-styles-orders/:id"
                                        parentSection="wholesale"
                                        component={CancelledStylesOrderPage}
                                        wholesaleVisible={false}
                                        onEnter={chainMiddleware([
                                            requireFullSignIn,
                                            hasPermission("manage_cancelled_styles"),
                                            checkPreference("cancelled_styles"),
                                        ])}
                                    />
                                    {flags.smartOrdersPage && (
                                        <Route
                                            path="replenishment-recommendations"
                                            parentSection="wholesale"
                                            component={SummaryOrdersPage}
                                            wholesaleVisible={false}
                                            onEnter={chainMiddleware([
                                                requireFullSignIn,
                                                checkUserType("brand"),
                                                hasPermission("manage_order_recommendations"),
                                                checkPreference("insight_reports"),
                                            ])}
                                        />
                                    )}
                                    {flags.smartOrdersPage && (
                                        <Route
                                            path="replenishment-recommendations/:smartOrderId"
                                            parentSection="wholesale"
                                            component={SmartOrderDetailsPage}
                                            wholesaleVisible={false}
                                            onEnter={chainMiddleware([
                                                requireFullSignIn,
                                                checkUserType("brand"),
                                                hasPermission("manage_order_recommendations"),
                                                checkPreference("insight_reports"),
                                            ])}
                                        />
                                    )}
                                    <Route
                                        path="cancelled-styles"
                                        parentSection="wholesale"
                                        component={CancelledStylesPage}
                                        wholesaleVisible={false}
                                        onEnter={chainMiddleware([
                                            requireFullSignIn,
                                            hasPermission("manage_cancelled_styles"),
                                            checkPreference("cancelled_styles"),
                                        ])}
                                    />
                                    <Route
                                        path="cancelled-styles/:id"
                                        parentSection="wholesale"
                                        component={CancelledStylePage}
                                        wholesaleVisible={false}
                                        onEnter={chainMiddleware([
                                            requireFullSignIn,
                                            hasPermission("manage_cancelled_styles"),
                                            checkPreference("cancelled_styles"),
                                        ])}
                                    />
                                    <Route
                                        path="checkout/mobile/order_group/:order_group"
                                        parentSection="wholesale"
                                        component={MobilePaymentsWrapperContainer}
                                        wholesaleVisible={false}
                                        hideHeader={true}
                                        onEnter={chainMiddleware([
                                            requireFullSignIn,
                                            checkUserType("not-nuorder"),
                                            notImpersonating,
                                        ])}
                                    />

                                    <Route
                                        path="lightspeed-reports"
                                        parentSection="wholesale"
                                        component={LightspeedReports}
                                        wholesaleVisible={false}
                                        onEnter={chainMiddleware([
                                            requireFullSignIn,
                                            notImpersonating,
                                            checkUserType("not-nuorder"),
                                            hasLsProcessor,
                                            hasPermission("can_access_payments_management"),
                                        ])}
                                    />
                                    <Route
                                        path="invoices"
                                        parentSection="wholesale"
                                        component={(props) =>
                                            FeatureFlaggedComponent({
                                                ...props,
                                                component: InvoicesContainer,
                                                flag: "paymentsInvoicesMenuItemNu63696",
                                            })
                                        }
                                        wholesaleVisible={false}
                                        onEnter={chainMiddleware([
                                            requireFullSignIn,
                                            notImpersonating,
                                            checkUserType("not-nuorder"),
                                            hasActiveProcessor,
                                            invoicesMenuItemAccess,
                                            trackInvoicePageAccess,
                                        ])}
                                    />
                                    <Route
                                        path="campaigns"
                                        wholesaleVisible={false}
                                        component={CampaignsContainer}
                                        onEnter={chainMiddleware([
                                            checkUserType("brand"),
                                            hasPermission("view_campaigns"),
                                        ])}
                                    />
                                    <Route
                                        path="campaign/:campaign"
                                        wholesaleVisible={false}
                                        component={CampaignContainer}
                                        onEnter={chainMiddleware([
                                            checkUserType("brand"),
                                            hasPermission("view_campaigns"),
                                        ])}
                                    />
                                    <Route
                                        path="smart-group(/:id)"
                                        wholesaleVisible={false}
                                        component={SmartGroupContainer}
                                        onEnter={chainMiddleware([
                                            checkUserType("brand"),
                                            hasPermission("view_campaigns"),
                                        ])}
                                    />
                                    <Route
                                        path="static-group(/:id)"
                                        wholesaleVisible={false}
                                        component={StaticGroupContainer}
                                        onEnter={chainMiddleware([
                                            checkUserType("brand"),
                                            hasPermission("view_campaigns"),
                                        ])}
                                    />
                                    <Route
                                        path="contact-groups"
                                        wholesaleVisible={false}
                                        component={SmartGroupsContainer}
                                        onEnter={checkUserType("brand")}
                                    />
                                    <Route
                                        path="order/:order/linesheetPdf/:id"
                                        parentSection="wholesale"
                                        wholesaleVisible={false}
                                        component={DownloadPage}
                                        onEnter={redirectToLegacy}
                                    />
                                    <Route
                                        path="order/:orderId/download/:template"
                                        parentSection="wholesale"
                                        wholesaleVisible={false}
                                        component={DownloadPage}
                                        onEnter={redirectToLegacy}
                                    />

                                    <Route
                                        path="admin/brand"
                                        wholesaleVisible={false}
                                        component={
                                            flags.isTransifexEnabledNu68053
                                                ? AdminIndexContainer
                                                : AdminLegacyLocalesIndexContainer
                                        }
                                        onEnter={chainMiddleware([canAccessModule("admin"), requireFullSignIn])}
                                    />

                                    <Route
                                        path="admin/brand/vp/info"
                                        component={PageNotFound}
                                        wholesaleVisible={false}
                                    />

                                    <Route
                                        title="Admin"
                                        subdomain="admin"
                                        uri="nuorder/brand/:id"
                                        path="admin/brand/vp/bulkimage"
                                        wholesaleVisible={false}
                                        component={LegacyContainer}
                                        // prettier-ignore
                                        onEnter={chainMiddleware([
                                            requireFullSignIn,
                                            checkPreference("unlimited_images", false),
                                            canAccessModule("admin"),
                                            checkPreference("virtual_showroom", false),
                                        ])}
                                    />

                                    <Route
                                        title="Admin"
                                        subdomain="admin"
                                        uri="nuorder/brand/:id"
                                        path="admin/brand/vp/orderexportinfo"
                                        wholesaleVisible={false}
                                        component={LegacyContainer}
                                        // prettier-ignore
                                        onEnter={chainMiddleware([
                                            requireFullSignIn,
                                            canAccessModule("admin"),
                                            hasPermission("edit_order_export_info"),
                                        ])}
                                    />

                                    <Route
                                        title="Admin"
                                        subdomain="admin"
                                        uri="nuorder/brand/:id"
                                        path="admin/brand/vp/product_visibility_report"
                                        wholesaleVisible={false}
                                        component={ProductVisibilityReport}
                                        // prettier-ignore
                                        onEnter={chainMiddleware([
                                            requireFullSignIn,
                                            canAccessModule("admin"),
                                            hasPermission("product_visibility_report"),
                                        ])}
                                    />

                                    <Route
                                        title="Admin"
                                        subdomain="admin"
                                        uri="nuorder/brand/:id"
                                        path="admin/brand/vp/image_management"
                                        wholesaleVisible={false}
                                        component={LegacyContainer}
                                        // prettier-ignore
                                        onEnter={chainMiddleware([
                                            requireFullSignIn,
                                            canAccessModule("admin"),
                                            checkPreference("virtual_showroom", false),
                                        ])}
                                    />

                                    <Route
                                        title="Admin"
                                        subdomain="admin"
                                        uri="nuorder/brand/:id"
                                        path="admin/brand/vp/paymenthistory"
                                        wholesaleVisible={false}
                                        component={PaymentHistory}
                                        onEnter={chainMiddleware([canAccessModule("admin"), requireFullSignIn])}
                                    />

                                    <Route
                                        path="management/permissions"
                                        wholesaleVisible={false}
                                        component={PermissionsManagementSettings}
                                        onEnter={chainMiddleware([
                                            canAccessModule("management"),
                                            requireFullSignIn,
                                            redirectToPermissions,
                                        ])}
                                    />
                                    <Route
                                        title="Admin"
                                        subdomain="admin"
                                        uri="nuorder/brand/:id"
                                        path="admin/brand/vp/:component"
                                        wholesaleVisible={false}
                                        component={LegacyContainer}
                                        onEnter={chainMiddleware([canAccessModule("admin"), requireFullSignIn])}
                                    />

                                    <Route
                                        path="admin/barcode-management"
                                        wholesaleVisible={false}
                                        component={BarcodeSettingsContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("admin"),
                                            requireFullSignIn,
                                            hasPermission("update_brand"),
                                        ])}
                                    />

                                    <Route
                                        path="admin/image-reports"
                                        wholesaleVisible={false}
                                        component={ImageReportsContainer}
                                        onEnter={chainMiddleware([canAccessModule("admin"), requireFullSignIn])}
                                    />

                                    <Route
                                        path="admin/image-settings"
                                        wholesaleVisible={false}
                                        component={ImageSettingsContainer}
                                        onEnter={chainMiddleware([
                                            requireFullSignIn,
                                            canAccessModule("admin"),
                                            hasPermission("update_brand_base"),
                                        ])}
                                    />

                                    <Route
                                        path="admin/payment-processing"
                                        wholesaleVisible={false}
                                        component={PaymentProcessingContainer}
                                        onEnter={chainMiddleware([
                                            requireFullSignIn,
                                            canAccessModule("admin"),
                                            hasPermission("modify_payment_account"),
                                        ])}
                                    />

                                    <Route
                                        path="admin/color-swatches"
                                        wholesaleVisible={false}
                                        component={ColorSwatchesPage}
                                        onEnter={chainMiddleware([
                                            requireFullSignIn,
                                            canAccessModule("admin"),
                                            hasPermission("update_product"),
                                            checkPreference("group_products"),
                                        ])}
                                    />

                                    <Route
                                        path="admin/brand-settings"
                                        wholesaleVisible={false}
                                        component={BrandSettingsContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("admin"),
                                            requireFullSignIn,
                                            hasPermission("update_brand_base"),
                                        ])}
                                    />
                                    {flags.showBrandAdminDataflow && (
                                        <Route
                                            path="admin/manage-data-sync/:merchantId/edit-mapping"
                                            wholesaleVisible={false}
                                            component={ManageEditMappingContainer}
                                            onEnter={chainMiddleware([
                                                canAccessModule("admin"),
                                                requireFullSignIn,
                                                hasPermission("dataflow_self_service"),
                                            ])}
                                        />
                                    )}
                                    {flags.showBrandAdminDataflow && (
                                        <Route
                                            path="admin/manage-data-sync"
                                            wholesaleVisible={false}
                                            component={ManageDataSyncContainer}
                                            onEnter={chainMiddleware([
                                                canAccessModule("admin"),
                                                requireFullSignIn,
                                                hasPermission("dataflow_self_service"),
                                            ])}
                                        />
                                    )}

                                    <Route
                                        path="admin/terms-and-conditions"
                                        wholesaleVisible={false}
                                        component={TermsAndConditionsContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("admin"),
                                            requireFullSignIn,
                                            hasPermission("update_brand"),
                                        ])}
                                    />

                                    <Route
                                        path="admin/shipping-management"
                                        wholesaleVisible={false}
                                        component={ShippingManagerContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("admin"),
                                            requireFullSignIn,
                                            hasPermission("edit_shipping_settings"),
                                        ])}
                                    />

                                    <Route
                                        path="admin/pricing"
                                        wholesaleVisible={false}
                                        component={PricingContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("admin"),
                                            requireFullSignIn,
                                            hasPermission("update_brand"),
                                        ])}
                                    />

                                    <Route
                                        path="admin/size-management"
                                        wholesaleVisible={false}
                                        component={SizeManagerContainer}
                                        onEnter={chainMiddleware([canAccessModule("admin"), requireFullSignIn])}
                                    />

                                    <Route
                                        path="admin/shipping-account/carrier(/:id)"
                                        wholesaleVisible={false}
                                        component={ShippingAccountCarrierContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("admin"),
                                            requireFullSignIn,
                                            hasPermission("edit_shipping_settings"),
                                        ])}
                                    />

                                    <Route
                                        path="admin/promotions"
                                        wholesaleVisible={false}
                                        component={PromotionsContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("admin"),
                                            requireFullSignIn,
                                            hasPermission("edit_shipping_settings"),
                                        ])}
                                    />

                                    <Route
                                        path="admin/promotion(/:id)"
                                        wholesaleVisible={false}
                                        component={PromotionContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("admin"),
                                            requireFullSignIn,
                                            hasPermission("edit_shipping_settings"),
                                        ])}
                                    />

                                    <Route
                                        path="admin/order-settings"
                                        wholesaleVisible={false}
                                        component={OrderSettingsContainer}
                                        onEnter={chainMiddleware([canAccessModule("admin"), requireFullSignIn])}
                                    />

                                    <Route
                                        path="admin/email-management"
                                        wholesaleVisible={false}
                                        component={EmailImageSettingsContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("admin"),
                                            requireFullSignIn,
                                            hasPermission("update_brand_base"),
                                        ])}
                                    />

                                    <Route
                                        path="admin/order-rules"
                                        wholesaleVisible={false}
                                        component={OrderMinMaxSettingsContainer}
                                        onEnter={chainMiddleware([canAccessModule("admin"), requireFullSignIn])}
                                    />

                                    <Route
                                        path="admin/inventory-settings"
                                        wholesaleVisible={false}
                                        component={InventorySettingsContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("admin"),
                                            requireFullSignIn,
                                            hasPermission("update_brand"),
                                            hasPermission("can_manage_inventory"),
                                        ])}
                                    />

                                    <Route
                                        path="admin/customer-user-groups"
                                        wholesaleVisible={false}
                                        component={CustomerUserGroupsContainer}
                                        onEnter={chainMiddleware([canAccessModule("admin"), requireFullSignIn])}
                                    />

                                    <Route
                                        path="admin/online-trade-show"
                                        wholesaleVisible={false}
                                        component={OnlineTradeShowContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("admin"),
                                            checkPreference("allow_guest_buyers"),
                                            requireFullSignIn,
                                        ])}
                                    />

                                    <Route
                                        path="admin/matterport-integration"
                                        wholesaleVisible={false}
                                        component={MatterportIntegrationContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("admin"),
                                            hasPermission("update_brand"),
                                            checkPreference("spaces_3d"),
                                            requireFullSignIn,
                                        ])}
                                    />

                                    <Route
                                        path="admin/bulk-buyer"
                                        wholesaleVisible={false}
                                        component={BuyerDataPageContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("admin"),
                                            requireFullSignIn,
                                            hasPermission("base_add_company_user"),
                                        ])}
                                    />
                                    <Route
                                        path="admin/bulk-product"
                                        wholesaleVisible={false}
                                        component={ProductDataPageContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("admin"),
                                            requireFullSignIn,
                                            hasPermission("update_product"),
                                        ])}
                                    />

                                    <Route
                                        path="admin/bulk-company"
                                        wholesaleVisible={false}
                                        component={CompanyDataPageContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("admin"),
                                            requireFullSignIn,
                                            // TODO: find out correct permissions
                                            // hasPermission("create_new_product"),
                                            // hasPermission("update_product"),
                                        ])}
                                    />

                                    <Route
                                        path="admin/bulk-inventory"
                                        wholesaleVisible={false}
                                        component={InventoryDataPageContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("admin"),
                                            requireFullSignIn,
                                            hasPermission("update_brand"),
                                            hasPermission("can_manage_inventory"),
                                        ])}
                                    />

                                    <Route
                                        path="admin/price-sheets"
                                        wholesaleVisible={false}
                                        component={PriceSheetsPageContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("admin"),
                                            requireFullSignIn,
                                            hasPermission("update_brand_base"),
                                            checkPreference("use_pricing_templates"),
                                        ])}
                                    />

                                    <Route
                                        path="admin/product-images"
                                        wholesaleVisible={false}
                                        component={ProductImagesContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("admin"),
                                            requireFullSignIn,
                                            checkPreference("unlimited_images"),
                                            hasPermission("update_brand"),
                                        ])}
                                    />
                                    <Route
                                        path="admin/size-packs"
                                        wholesaleVisible={false}
                                        component={SizePacksContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("packs"),
                                            requireFullSignIn,
                                            hasPermission("update_brand"),
                                        ])}
                                    />

                                    <Route
                                        path="admin/promotions/manage-promotions/:promotionType"
                                        wholesaleVisible={false}
                                        component={ManagePromotionsContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("admin"),
                                            requireFullSignIn,
                                            checkPreference("enable_advanced_pricing"),
                                            hasPermission("promotions_access"),
                                        ])}
                                    />
                                    <Route
                                        path="admin/promotions/list"
                                        wholesaleVisible={false}
                                        component={ManagePromotionsListContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("admin"),
                                            requireFullSignIn,
                                            // checkPreference("use_advance_pricing_for_all_prices"),
                                            checkPreference("enable_advanced_pricing"),
                                            hasPermission("promotions_access"),
                                        ])}
                                    />
                                    <Route
                                        path="admin/promotions/manage-promotions/:promotionType/:id"
                                        wholesaleVisible={false}
                                        component={ManagePromotionsContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("admin"),
                                            requireFullSignIn,
                                            checkPreference("enable_advanced_pricing"),
                                        ])}
                                    />

                                    <Route
                                        path="admin/payment/payment-preferences"
                                        wholesaleVisible={false}
                                        component={PaymentPreferencesSettingsContainer}
                                        onEnter={chainMiddleware([
                                            canAccessModule("admin"),
                                            requireFullSignIn,
                                            hasPermission("can_update_payment_preferences"),
                                        ])}
                                    />

                                    <Route
                                        path="admin/payment/payment-configurations"
                                        wholesaleVisible={false}
                                        component={PaymenConfigurationsSettingsContainer}
                                        onEnter={chainMiddleware([
                                            requireFullSignIn,
                                            canAccessModule("payments"),
                                            checkUserType("nuorder"),
                                        ])}
                                    />

                                    <Route
                                        path="management/nuorder"
                                        wholesaleVisible={false}
                                        component={MgmtIndexContainer}
                                        onEnter={chainMiddleware([canAccessModule("management"), requireFullSignIn])}
                                    />
                                    <Route
                                        path="pages"
                                        parentSection="wholesale"
                                        wholesaleVisible={false}
                                        component={PageViewsContainer}
                                        onEnter={checkUserType("brand")}
                                    />
                                    <Route
                                        path="size-packs"
                                        parentSection="wholesale"
                                        wholesaleVisible={false}
                                        component={SizePacksUploadContainer}
                                        onEnter={checkUserType("brand")}
                                    />
                                    <Route
                                        path="page/:page"
                                        parentSection="wholesale"
                                        wholesaleVisible={false}
                                        component={BrandDashboardContainer}
                                    />
                                    <Route
                                        path="site/:slug"
                                        parentSection="wholesale"
                                        wholesaleVisible={false}
                                        component={LiveBrandSitePageDetailsContainer}
                                    >
                                        <Route
                                            path="page/:pageId"
                                            parentSection="wholesale"
                                            wholesaleVisible={false}
                                            component={LiveBrandSitePageDetailsContainer}
                                        />
                                    </Route>
                                    <Route
                                        path="request-impersonation"
                                        wholesaleVisible={false}
                                        component={RequestImpersonationContainer}
                                        onEnter={chainMiddleware([
                                            checkUserType("not-nuorder"),
                                            requireFullSignIn,
                                            redirectIfTempPassword,
                                        ])}
                                    />
                                    <Route
                                        path="stop-impersonation"
                                        wholesaleVisible={false}
                                        component={StopImpersonationContainer}
                                        onEnter={chainMiddleware([requireFullSignIn])}
                                    />
                                    <Route
                                        path="enter-impersonation"
                                        wholesaleVisible={false}
                                        component={EnterImpersonationContainer}
                                        onEnter={chainMiddleware([
                                            requireFullSignIn,
                                            checkUserType("nuorder"),
                                            setActiveBrandFromUrl,
                                            redirectIfTempPassword,
                                        ])}
                                    />
                                    <Route
                                        path="bulk-order-pdf/:id"
                                        uri="bulk-order-pdf"
                                        component={BulkOrderPDfPage}
                                        onEnter={chainMiddleware([checkUserType("not-guest-buyer")])}
                                        wholesaleVisible={false}
                                    />
                                </Route>
                                <Route
                                    path="shipment-review"
                                    parentSection="wholesale"
                                    wholesaleVisible={false}
                                    component={LegacyCartShipmentReviewContainer}
                                />
                                <Route
                                    path="cart/checkout/challenge/:paymentGroupId/:transactionId/:transactionToken"
                                    wholesaleVisible={false}
                                    hideHeader={true}
                                    component={CheckoutChallengeContainer}
                                />
                                <Route
                                    path="cart/checkout/challenge-complete/:paymentGroupId/:transactionId"
                                    wholesaleVisible={false}
                                    hideHeader={true}
                                    component={CheckoutChallengeContainer}
                                />
                            </Route>

                            <Route
                                path="brand-profile/:pageId/instagram-callback"
                                component={InstagramRedirectPage}
                                onEnter={checkUserType("brand")}
                            />
                            <Route
                                path="site/:id/page/:pageId/settings"
                                title="Site Details"
                                subdomain="settings"
                                uri="nuorder"
                                component={PageDetailsContainer}
                                onEnter={chainMiddleware([requireFullSignIn])}
                            />
                            <Route
                                path="site/:id/templates"
                                title="Page Templates"
                                subdomain="settings"
                                uri="nuorder"
                                component={TemplatePagesContainer}
                                onEnter={chainMiddleware([requireFullSignIn])}
                            />
                            <Route
                                path="site/:id/settings"
                                title="Site Details"
                                subdomain="settings"
                                uri="nuorder"
                                component={SiteDetailsContainer}
                                onEnter={chainMiddleware([requireFullSignIn])}
                            />

                            <Route
                                path="sites"
                                title="Sites Manager"
                                subdomain="settings"
                                uri="nuorder"
                                component={SitesManagerContainer}
                                onEnter={chainMiddleware([requireFullSignIn])}
                            />

                            <Route
                                path="terms-and-conditions"
                                title="Terms and Conditions"
                                subdomain="terms"
                                uri="nuorder"
                                component={MobileTermsAndConditions}
                                onEnter={chainMiddleware([requireFullSignIn])}
                            />

                            <Route
                                path="admin"
                                title="Admin"
                                subdomain="admin"
                                uri="nuorder/brand/:id"
                                component={LegacyContainer}
                                onEnter={chainMiddleware([adminRoute(), canAccessModule("admin"), requireFullSignIn])}
                                wholesaleVisible={false}
                            />
                            <Route
                                path="admin/templates"
                                title="Order Export Templates"
                                subdomain="templates"
                                uri="nuorder/brand/:id"
                                component={CustomTemplates}
                                onEnter={chainMiddleware([canAccessModule("admin"), requireFullSignIn])}
                            />
                            <Route
                                path="admin/templates/:id"
                                component={TemplatePage}
                                onEnter={checkUserType("brand")}
                            />
                            {/* TODO: make sure permissions and preferences are picked correctly */}
                            <Route
                                path="admin/ats-reports-templates"
                                title="ATS Reports Templates"
                                subdomain="templates"
                                uri="nuorder/brand/:id"
                                component={AtsCustomTemplateList}
                                onEnter={chainMiddleware([
                                    canAccessModule("admin"),
                                    requireFullSignIn,
                                    hasPermission("update_brand"),
                                ])}
                            />
                            <Route
                                path="admin/ats-reports-templates/:id"
                                component={AtsCustomTemplate}
                                onEnter={checkUserType("brand")}
                            />
                            {flags.newReportingPage && (
                                <Route
                                    path="reporting"
                                    title="Reporting"
                                    uri="nuorder/brand/:id"
                                    component={ReportingPage}
                                    onEnter={chainMiddleware([canAccessModule("reporting"), requireFullSignIn])}
                                />
                            )}
                            {flags.newReportingPage && (
                                <Route
                                    path="sell-through"
                                    title="Sell-Through"
                                    uri="nuorder/brand/:id"
                                    component={SellThroughReportingPage}
                                    onEnter={chainMiddleware([
                                        canAccessModule("reporting"),
                                        hasPermission("sell_through"),
                                        requireFullSignIn,
                                    ])}
                                />
                            )}
                            {flags.newReportingPage && (
                                <Route
                                    path="low-inventory"
                                    title="Low Inventory"
                                    uri="nuorder/brand/:id"
                                    component={LowInventoryReportingPage}
                                    onEnter={chainMiddleware([
                                        canAccessModule("reporting"),
                                        hasPermission("low_inventory"),
                                        requireFullSignIn,
                                    ])}
                                />
                            )}
                            <Route
                                path={`${flags.newReportingPage ? "reporting-legacy" : "reporting"}`}
                                title="Reporting"
                                subdomain="reporting"
                                uri="nuorder/brand/:id"
                                component={LegacyContainer}
                                onEnter={chainMiddleware([canAccessModule("reporting"), requireFullSignIn])}
                            />
                            <Route
                                path="management"
                                title="Brand Management"
                                subdomain="management"
                                uri="nuorder/brand/:id"
                                component={LegacyContainer}
                                onEnter={chainMiddleware([
                                    canAccessModule("management"),
                                    requireFullSignIn,
                                    redirectToPermissions,
                                ])}
                            />
                            <Route
                                path="management/inventory-merchant-settings"
                                title="Merchant Inventory Settings"
                                subdomain="management"
                                uri="nuorder/brand/:id"
                                component={MerchantInventorySettingsContainer}
                                onEnter={chainMiddleware([
                                    checkUserType("nuorder"),
                                    canAccessModule("management"),
                                    requireFullSignIn,
                                ])}
                            />
                            <Route
                                path="management/customization-settings"
                                title="Customization Settings"
                                subdomain="management"
                                uri="nuorder/brand/:id"
                                component={CustomizationSettingsContainer}
                                onEnter={chainMiddleware([
                                    checkUserType("nuorder"),
                                    canAccessModule("management"),
                                    checkPreference("allow_product_customization_templates"),
                                    requireFullSignIn,
                                ])}
                            />
                            <Route
                                path="management/internal-notes"
                                title="Brand Notes"
                                subdomain="management"
                                uri="nuorder/brand/:id"
                                component={InternalNotes}
                                onEnter={chainMiddleware([canAccessModule("management"), requireFullSignIn])}
                            />
                            <Route
                                path="management/lifecycle-scripts"
                                title="Brand Lifecycle Scripts"
                                subdomain="management"
                                uri="nuorder/brand/:id"
                                component={LifecycleScripts}
                                onEnter={chainMiddleware([canAccessModule("management"), requireFullSignIn])}
                            />
                            <Route
                                path="management/schema-mapper"
                                title="Schema Mapper"
                                subdomain="management"
                                uri="nuorder/brand/:id"
                                component={SchemaMapper}
                                onEnter={chainMiddleware([canAccessModule("management"), requireFullSignIn])}
                            />
                            <Route
                                path="admin/custom-branding"
                                title="Brand Notes"
                                subdomain="admin"
                                uri="nuorder/brand/:id"
                                component={ImagesManagementContainer}
                                onEnter={chainMiddleware([
                                    canAccessModule("admin"),
                                    requireFullSignIn,
                                    hasPermission("update_brand_base"),
                                ])}
                            />
                            <Route
                                path="admin/manage-logo"
                                title="Brand Notes"
                                subdomain="admin"
                                uri="nuorder/brand/:id"
                                component={ManageLogoContainer}
                                onEnter={chainMiddleware([
                                    canAccessModule("admin"),
                                    requireFullSignIn,
                                    hasPermission("update_brand_base"),
                                ])}
                            />
                            <Route
                                path="admin/watermarking"
                                title="Watermarking"
                                subdomain="admin"
                                uri="nuorder/brand/:id"
                                component={WatermarkingContainer}
                                onEnter={chainMiddleware([
                                    canAccessModule("admin"),
                                    requireFullSignIn,
                                    hasPermission("update_brand_base"),
                                    checkPreference("add_watermarking"),
                                ])}
                            />

                            <Route
                                path="management/preferred-retailers"
                                title="Preferred Retailers"
                                subdomain="management"
                                uri="nuorder/brand/:id"
                                wholesaleVisible={false}
                                component={PreferredRetailersContainer}
                                onEnter={chainMiddleware([
                                    canAccessModule("management"),
                                    requireFullSignIn,
                                    preferredRetailers,
                                ])}
                            />

                            <Route
                                path="profile"
                                uri="profile"
                                component={ProfileContainer}
                                onEnter={chainMiddleware([requireFullSignIn, activeCompany])}
                            />
                            <Route
                                path="payment-methods"
                                uri="payment-methods"
                                component={ManagePaymentMethodsContainer}
                                onEnter={chainMiddleware([requireFullSignIn, activeCompany])}
                            />
                            <Redirect from="credit-cards" to="payment-methods" />

                            <Route path="*" component={PageNotFound} />
                        </Route>
                        <Route
                            path="the-wall"
                            title="The Wall"
                            subdomain="buyer"
                            uri="nuorder"
                            component={LegacyContainer}
                        />
                        <Route
                            path="the-wall/settings"
                            title="Wall Settings"
                            subdomain="manage"
                            uri="nuorder"
                            component={LegacyContainer}
                            onEnter={chainMiddleware([requireFullSignIn, activeCompany])}
                        />
                        <Route
                            path="/settings/organizations/:retailerId"
                            title="Retailer Settings"
                            subdomain="manage"
                            uri="nuorder"
                            component={(props) =>
                                FeatureFlaggedComponent({
                                    ...props,
                                    fallbackComponent: PageNotFound,
                                    component: MerchantSettingsTemplate,
                                    flag: "merchantOrgNewProfileSettingsPageNu70852",
                                })
                            }
                            onEnter={chainMiddleware([requireFullSignIn, activeCompany])}
                        >
                            <IndexRoute component={MerchantSettingsGeneralPage} />
                            <Route path="general" component={MerchantSettingsGeneralPage} />
                            <Route path="store-profile" component={MerchantSettingsStoreProfilePage} />
                        </Route>
                        <Route
                            path="the-wall/settings/b2b/manage-merchants"
                            title="B2B Merchant Management"
                            subdomain="buyer"
                            uri="nuorder"
                            onEnter={chainMiddleware([requireFullSignIn, merchantManagement])}
                            component={(props) =>
                                FeatureFlaggedComponent({
                                    ...props,
                                    component: B2BMerchantManagement,
                                    flag: "enableMerchantOrganizationFlowNu58916",
                                })
                            }
                        />
                        <Route
                            path="the-wall/settings/b2b"
                            title="B2B"
                            subdomain="manage"
                            uri="nuorder"
                            onEnter={chainMiddleware([requireFullSignIn, hasPOSIntegration, merchantEnablements])}
                            component={B2BMerchantEnablementWrapper}
                        >
                            <IndexRoute component={B2BMerchantEnablement} />
                            <Route path="integration-settings" component={B2BIntegrationSettings} />
                        </Route>
                        <Route
                            path="site/:id/page/:pageId/settings"
                            title="Site Details"
                            subdomain="settings"
                            uri="nuorder"
                            component={PageDetailsContainer}
                            onEnter={chainMiddleware([requireFullSignIn])}
                        />
                        <Route
                            path="site/:id/settings"
                            title="Site Details"
                            subdomain="settings"
                            uri="nuorder"
                            component={SiteDetailsContainer}
                            onEnter={chainMiddleware([requireFullSignIn])}
                        />
                        <Route
                            path="site/:id/templates"
                            title="Page Templates"
                            subdomain="settings"
                            uri="nuorder"
                            component={TemplatePagesContainer}
                            onEnter={chainMiddleware([requireFullSignIn])}
                        />
                        <Route
                            path="sites"
                            title="Sites Manager"
                            subdomain="settings"
                            uri="nuorder"
                            component={SitesManagerContainer}
                            onEnter={chainMiddleware([requireFullSignIn])}
                        />
                        <Route
                            path="marketplace"
                            title="Find New Accounts"
                            subdomain="marketplace"
                            uri="marketplace"
                            component={LegacyContainer}
                            onEnter={canAccessModule("marketplace")}
                        />
                        <Route
                            path="management"
                            title="NuORDER Utilities"
                            subdomain="management"
                            uri="nuorder/brand/create_new"
                            component={LegacyContainer}
                            onEnter={chainMiddleware([checkUserType("nuorder"), requireFullSignIn])}
                            wholesaleVisible={false}
                        />
                        <Route
                            path="/management/utilities"
                            component={MgmtUtilitiesContainer}
                            onEnter={chainMiddleware([checkUserType("nuorder"), removeActiveBrand, requireFullSignIn])}
                        />
                        <Route
                            path="/management/utilities/dataflow-management"
                            subdomain="management"
                            component={DataflowManagementContainer}
                            onEnter={chainMiddleware([checkUserType("nuorder"), removeActiveBrand, requireFullSignIn])}
                        />
                        <Route
                            path="/management/utilities/user-information-lookup"
                            subdomain="management"
                            component={UserInformationLookupContainer}
                            onEnter={chainMiddleware([checkUserType("nuorder"), removeActiveBrand, requireFullSignIn])}
                        />
                        <Route
                            path="/management/utilities/active-status-lookup"
                            subdomain="management"
                            component={ActiveStatusLookupContainer}
                            onEnter={chainMiddleware([checkUserType("nuorder"), removeActiveBrand, requireFullSignIn])}
                        />
                        <Route
                            path="/management/utilities/manage-users"
                            subdomain="management"
                            component={MgmtUtilitiesManageUsersContainer}
                            onEnter={chainMiddleware([checkUserType("nuorder"), removeActiveBrand, requireFullSignIn])}
                        />
                        <Route
                            path="/management/utilities/disable-users"
                            subdomain="management"
                            component={MgmtUtilitiesDisableUsersContainer}
                            onEnter={chainMiddleware([checkUserType("nuorder"), removeActiveBrand, requireFullSignIn])}
                        />
                        <Route
                            path="management/utilities/assortments-management"
                            title="Assortments Management"
                            subdomain="management"
                            uri="nuorder"
                            component={AssortmentsManagement}
                            onEnter={chainMiddleware([canAccessModule("management"), requireFullSignIn])}
                        />
                        <Route
                            path="management/utilities/assortments-management-superusers"
                            title="Assortments Management: Superusers"
                            subdomain="management"
                            uri="nuorder"
                            component={AssortmentsManagementSuperusers}
                            onEnter={chainMiddleware([canAccessModule("management"), requireFullSignIn])}
                        />
                        <Route
                            path="management/utilities/merchants-management"
                            title="Merchants Management"
                            subdomain="management"
                            uri="nuorder"
                            component={MerchantsManagement}
                            onEnter={chainMiddleware([canAccessModule("management"), requireFullSignIn])}
                        />
                        <Route
                            path="management/utilities/product-sizes-overrides"
                            title="Product Sizes Overrides"
                            subdomain="management"
                            uri="nuorder"
                            component={ProductSizesOverridesContainer}
                            onEnter={chainMiddleware([canAccessModule("management"), requireFullSignIn])}
                        />
                        <Route
                            path="management/utilities/assortments-support"
                            title="Assortments Support"
                            subdomain="management"
                            uri="nuorder"
                            component={AssortmentsSupport}
                            onEnter={chainMiddleware([canAccessModule("management"), requireFullSignIn])}
                        />

                        <Route
                            path="management/utilities/size-families"
                            title="Size Families Management"
                            subdomain="management"
                            uri="nuorder"
                            component={CustomerSizeFamiliesContainer}
                            onEnter={chainMiddleware([canAccessModule("management"), requireFullSignIn])}
                        />
                        <Route
                            path="management/utilities/nav-sites"
                            title="Site Navigation Sites"
                            subdomain="management"
                            uri="nuorder"
                            component={NavSites}
                            onEnter={chainMiddleware([canAccessModule("management"), requireFullSignIn])}
                        />
                    </Route>
                </Route>
                <Route
                    path="/merchant-deactivated"
                    component={MerchantIsDeactive}
                    onEnter={chainMiddleware([requireFullSignIn, redirectIfTempPassword])}
                />
            </Route>
            <Route onEnter={chainMiddleware([redirectIfTempPassword, setActiveBrandFallback])}>
                <Route path="expired-forgot-password" component={PasswordResetInvalid} />
                <Route path="confirm-password-reset" component={PasswordReset} />
                <Route path="forgot-password" component={ForgotPassword} />
                <Route path="signup" component={SignupContainer} />
                <Route path="sso" component={LoginSSOContainer} />
                <Route path="404" component={PageNotFound} />
                <Route path="403" component={PageNotFound} featureIsDisabled={true} />
                <Route path="/:custom" component={LoginContainer} />
                <Route path="*" component={PageNotFound} />
            </Route>
        </Route>
    );
};
